import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Check, Copy, Eye, History, ImagePlus, Loader2, RotateCcw, Send, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });

const KIND_BADGE = {
  price: "Preis",
  text: "Text",
  color: "Farbe",
  section: "Sektion",
  portfolio: "Portfolio",
};

export default function AdminAiManager() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState("");
  const [draft, setDraft] = useState(null);
  const [history, setHistory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const chatEndRef = useRef(null);
  const fileRef = useRef(null);

  const loadDraft = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ai/draft");
      setDraft(data);
    } catch { /* ignore */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ai/history");
      setHistory(data);
    } catch { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ai/messages");
      setMessages(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadMessages();
    loadDraft();
    loadHistory();
  }, [loadMessages, loadDraft, loadHistory]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setStreaming("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Fehler ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let donePayload = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5)); } catch { continue; }
          if (evt.type === "delta") {
            setStreaming((s) => s + evt.text);
          } else if (evt.type === "error") {
            throw new Error(evt.message);
          } else if (evt.type === "done") {
            donePayload = evt;
          }
        }
      }
      if (donePayload) {
        // JSON-Block aus der gestreamten Anzeige entfernen, saubere Antwort zeigen
        setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: donePayload.reply, staged: donePayload.staged?.length || 0, created_at: new Date().toISOString() }]);
        if (donePayload.staged?.length) {
          toast.success(`${donePayload.staged.length} Änderung(en) im Entwurf — bitte Vorschau prüfen.`);
        }
        if (donePayload.rejected?.length) {
          const reasons = donePayload.rejected.map((r) => r.reason).join(" · ");
          toast.warning(`${donePayload.rejected.length} Änderung(en) abgelehnt: ${reasons}`);
        }
      }
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "KI-Anfrage fehlgeschlagen.");
      loadMessages();
    } finally {
      setStreaming("");
      setSending(false);
      loadDraft();
      loadMessages();
    }
  };

  const apply = async () => {
    if (!window.confirm("Alle Entwurfs-Änderungen JETZT live schalten?")) return;
    try {
      const { data } = await api.post("/admin/ai/apply");
      toast.success(`Live geschaltet — Version ${data.version} (${data.applied_changes} Änderungen).`);
      loadDraft();
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const discard = async () => {
    if (!window.confirm("Entwurf wirklich verwerfen? Alle offenen Änderungen gehen verloren.")) return;
    try {
      await api.post("/admin/ai/discard");
      toast.success("Entwurf verworfen.");
      loadDraft();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rollback = async (version) => {
    if (!window.confirm(`Website-Inhalte auf Version ${version} zurücksetzen?`)) return;
    try {
      const { data } = await api.post(`/admin/ai/rollback/${version}`);
      toast.success(`Zurückgesetzt auf Version ${version} (jetzt Version ${data.version}).`);
      loadDraft();
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/ai/upload", fd);
      setUploadedUrl(data.url);
      toast.success("Bild hochgeladen — URL unten kopieren und dem KI-Manager schicken.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copyUrl = () => {
    const full = `${process.env.REACT_APP_BACKEND_URL}${uploadedUrl}`;
    navigator.clipboard.writeText(uploadedUrl).then(() => toast.success("URL kopiert.")).catch(() => toast.info(full));
  };

  const changes = draft?.changes || [];

  return (
    <div data-testid="ki-manager" className="space-y-6">
      {/* Kopf */}
      <div className="border border-goldlight/30 bg-goldlight/5 p-5">
        <p className="font-display uppercase tracking-tight text-lg flex items-center gap-2">
          <Bot size={20} className="text-goldlight" /> KI-Manager
          {draft?.has_draft && (
            <span data-testid="ki-draft-badge" className="text-[10px] uppercase tracking-[0.2em] border border-goldlight/60 text-goldlight px-2 py-0.5">
              Entwurf offen: {changes.length} Änderung{changes.length === 1 ? "" : "en"}
            </span>
          )}
        </p>
        <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
          Schreib dem KI-Manager einfach, was sich ändern soll — z. B. <span className="text-bone">„Mach das PLUS-Paket 799 Euro"</span>,{" "}
          <span className="text-bone">„Blend das FAQ aus"</span> oder <span className="text-bone">„Änder die Gold-Farbe zu #c9a227"</span>.
          Nichts geht sofort live: Jede Änderung landet erst im <span className="text-goldlight">Entwurf</span>, du prüfst die Vorschau und klickst „Übernehmen".
        </p>
      </div>

      {/* Offene Änderungen / Vorschau */}
      {draft?.has_draft && changes.length > 0 && (
        <div data-testid="ki-pending-panel" className="border border-goldlight/40 p-5 space-y-4">
          <p className="text-xs uppercase tracking-[0.25em] text-goldlight font-bold">
            Offene Änderungen (noch nicht live)
          </p>
          <ul className="space-y-2">
            {changes.map((c, i) => (
              <li key={i} data-testid={`ki-change-${i}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm border-b border-white/5 pb-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 w-20 shrink-0">{KIND_BADGE[c.kind] || c.kind}</span>
                <span className="text-bone">{c.label}</span>
                <span className="text-zinc-500 line-through">{c.old}</span>
                <span className="text-goldlight">→ {c.new}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2 pt-2">
            <a
              href="/?preview=1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ki-preview-btn"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:border-goldlight hover:text-goldlight transition-colors duration-300"
            >
              <Eye size={14} /> Vorschau öffnen
            </a>
            <button
              onClick={apply}
              data-testid="ki-apply-btn"
              className="inline-flex items-center gap-2 border border-emerald-300/40 text-emerald-300 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-emerald-300/10 transition-colors duration-300"
            >
              <Check size={14} /> Übernehmen — live schalten
            </button>
            <button
              onClick={discard}
              data-testid="ki-discard-btn"
              className="inline-flex items-center gap-2 border border-red-400/40 text-red-400 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-red-400/10 transition-colors duration-300"
            >
              <Trash2 size={14} /> Verwerfen
            </button>
          </div>
        </div>
      )}

      {/* Chat */}
      <div className="border border-white/10">
        <div data-testid="ki-chat-messages" className="max-h-[420px] overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && !streaming && (
            <p className="text-sm text-zinc-500 text-center py-8">
              Noch keine Unterhaltung. Schreib dem KI-Manager einfach los, z. B. „Was kannst du alles ändern?"
            </p>
          )}
          {messages.map((m, i) => (
            <div key={m.id || i} data-testid={`ki-message-${m.role}-${i}`} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && <Bot size={16} className="text-goldlight shrink-0 mt-1" />}
              <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-goldlight/10 border border-goldlight/30 text-bone" : "bg-white/5 border border-white/10 text-zinc-300"
              }`}>
                {m.content}
                {m.staged > 0 && (
                  <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-goldlight">→ {m.staged} Änderung{m.staged === 1 ? "" : "en"} im Entwurf</p>
                )}
              </div>
              {m.role === "user" && <User size={16} className="text-zinc-500 shrink-0 mt-1" />}
            </div>
          ))}
          {streaming && (
            <div className="flex gap-3" data-testid="ki-streaming">
              <Bot size={16} className="text-goldlight shrink-0 mt-1" />
              <div className="max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap bg-white/5 border border-white/10 text-zinc-300">
                {streaming}
                <span className="inline-block w-2 h-4 bg-goldlight/70 animate-pulse ml-1 align-middle" />
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <form onSubmit={send} className="border-t border-white/10 flex gap-2 p-3">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="ki-chat-input"
            placeholder="z. B.: Änder den PREMIUM-Preis auf 1.499 Euro …"
            className="flex-1 bg-transparent border border-white/10 px-4 py-3 text-sm text-bone placeholder:text-zinc-600 focus:outline-none focus:border-goldlight transition-colors duration-300"
            disabled={sending}
          />
          <button
            type="submit"
            data-testid="ki-chat-send"
            disabled={sending || !input.trim()}
            className="inline-flex items-center gap-2 border border-goldlight/60 text-goldlight px-5 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-goldlight/10 transition-colors duration-300 disabled:opacity-40"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Senden
          </button>
        </form>
      </div>

      {/* Bild-Upload für Portfolio */}
      <div className="border border-white/10 p-5 space-y-3">
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-bold flex items-center gap-2">
          <ImagePlus size={14} className="text-goldlight" /> Neues Bild fürs Portfolio hochladen
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" data-testid="ki-upload-input" className="text-sm text-zinc-400 file:mr-3 file:border file:border-white/15 file:bg-transparent file:text-bone file:px-4 file:py-2 file:text-[11px] file:uppercase file:tracking-[0.2em] hover:file:border-goldlight" />
          <button onClick={upload} disabled={uploading} data-testid="ki-upload-btn" className="inline-flex items-center gap-2 border border-goldlight/60 text-goldlight px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-goldlight/10 transition-colors duration-300 disabled:opacity-40">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Hochladen
          </button>
        </div>
        {uploadedUrl && (
          <div data-testid="ki-upload-result" className="flex flex-wrap items-center gap-2 border border-emerald-300/30 bg-emerald-300/5 p-3">
            <p className="text-sm text-emerald-300 break-all flex-1">{uploadedUrl}</p>
            <button onClick={copyUrl} data-testid="ki-upload-copy" className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-[10px] uppercase tracking-[0.2em] hover:border-goldlight hover:text-goldlight transition-colors duration-300">
              <Copy size={12} /> Kopieren
            </button>
          </div>
        )}
        <p className="text-xs text-zinc-500">
          Nach dem Hochladen dem KI-Manager z. B. schreiben: <span className="text-zinc-300">„Füg dieses Bild ins Portfolio: {uploadedUrl || "<URL>"} — Titel: Mein neues Design"</span>
        </p>
      </div>

      {/* Versions-Verlauf */}
      <div className="border border-white/10 p-5 space-y-3" data-testid="ki-history">
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-bold flex items-center gap-2">
          <History size={14} className="text-goldlight" /> Versions-Verlauf
          {history && <span className="text-zinc-600 normal-case tracking-normal">(aktuell: Version {history.current_version})</span>}
        </p>
        {!history || history.versions.length === 0 ? (
          <p className="text-sm text-zinc-500">Noch keine früheren Versionen gespeichert.</p>
        ) : (
          <ul className="space-y-2">
            {history.versions.map((v) => (
              <li key={v.version} className="flex flex-wrap items-center gap-3 text-sm border-b border-white/5 pb-2">
                <span className="text-bone font-medium">Version {v.version}</span>
                <span className="text-zinc-500 text-xs">{fmtDate(v.created_at)}</span>
                <span className="text-zinc-600 text-xs flex-1">{v.note}</span>
                <button
                  onClick={() => rollback(v.version)}
                  data-testid={`ki-rollback-${v.version}`}
                  className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:border-goldlight hover:text-goldlight transition-colors duration-300"
                >
                  <RotateCcw size={12} /> Wiederherstellen
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
